/**
 * **What the camera is doing**, and the fixed facts every scanner asks it for.
 *
 * Moved here from the member app's `member-in-event/constants.ts` when the check-in screen became
 * the second surface with a camera (`EVT-042` AC-6). The values are unchanged; what each app *says*
 * about a state stays in that app, because a member pasting a connect code and a volunteer typing
 * a ticket code are told different things.
 */
export const INITIAL_CAMERA_STATE = { kind: 'starting' };
/**
 * Which failure a `getUserMedia` rejection is.
 *
 * The names are the Media Capture spec's, read off `error.name` rather than off a message string —
 * messages differ by engine and are localised, and a substring match on one is how this stops
 * working in a language nobody on the team reads.
 *
 * `SecurityError` joins `NotAllowedError` as *denied*: both mean the person (or their policy) did
 * not let the page have the camera, which is one thing from where they are standing.
 * `NotFoundError` and `OverconstrainedError` are *unavailable* — there is no camera, or none
 * matching what was asked for. Everything else, including a decoder that would not load, is
 * *failed*: something broke, and the honest thing is to say so and offer the field.
 */
export function cameraFailureState(error) {
    const name = typeof error === 'object' &&
        error !== null &&
        typeof error.name === 'string'
        ? error.name
        : '';
    if (name === 'NotAllowedError' || name === 'SecurityError')
        return { kind: 'denied' };
    if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') {
        return { kind: 'unavailable' };
    }
    return { kind: 'failed' };
}
/** Is this a state in which the camera will not start without being asked again? */
export function cameraHasFailed(state) {
    return state.kind === 'denied' || state.kind === 'unavailable' || state.kind === 'failed';
}
/** How often a frame is read, in milliseconds. */
export const SCAN_FRAME_INTERVAL_MS = 250;
/**
 * The camera a scanner asks for: the one pointing away from the person holding the device.
 *
 * `ideal` rather than `exact`, and that is what makes a computer work: a laptop's only camera faces
 * the user, and `exact: 'environment'` would refuse it with an `OverconstrainedError` that reads as
 * "no camera". With `ideal` a phone gets its rear camera and a computer gets its built-in or
 * attached one. Never a microphone.
 */
export const SCANNER_CAMERA_CONSTRAINTS = {
    video: { facingMode: { ideal: 'environment' } },
    audio: false,
};
//# sourceMappingURL=camera-state.js.map